/*
 Copyright (C) 2019-2023 Thiemo Leonhardt, Klaus Ramm, Tom-Maurice Schreiber, Sören Schwab

 This program is free software: you can redistribute it and/or modify
 it under the terms of the GNU Affero General Public License as
 published by the Free Software Foundation, either version 3 of the
 License, or (at your option) any later version.

 This program is distributed in the hope that it will be useful,
 but WITHOUT ANY WARRANTY; without even the implied warranty of
 MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 GNU Affero General Public License for more details.

 You should have received a copy of the GNU Affero General Public License
 along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import { newElement } from "../helpers/domBuilding";
import {
  SOURCE_CODE_TRANSLATIONS,
  generateSourceCode,
  getSupportedCodeLanguages,
  isSourceCodeTranslatable,
} from "../helpers/sourceCode";
import { t } from "../i18n";

export class CodeView {
  constructor(presenter, domRoot) {
    this.presenter = presenter;
    this.domRoot = domRoot;
    this.lang = "--";
    this.sourcecodeActivatedFullWidth = false;
    this.translationMap = SOURCE_CODE_TRANSLATIONS;

    this.preRender();
  }

  preRender() {
    const sourcecode = document.getElementById("SourcecodeDisplay");
    const sourcecodeDisplay = document.createElement("div");
    sourcecodeDisplay.classList.add("fixFullWidth", "margin-top-small");
    const sourcecodeHeader = document.createElement("div");
    sourcecodeHeader.classList.add("columnAuto", "sourcecodeHeaderRow");
    const sourcecodeTitle = document.createElement("strong");
    sourcecodeTitle.classList.add("sourcecodeLabel");
    sourcecodeTitle.appendChild(document.createTextNode(t("code.translateTo")));
    const sourcecodeForm = document.createElement("div");
    sourcecodeForm.classList.add("sourcecodeSelectWrap");
    const sourcecodeSelect = document.createElement("select");
    sourcecodeSelect.classList.add("form-select");
    sourcecodeSelect.id = "SourcecodeSelect";
    sourcecodeSelect.addEventListener("change", (event) =>
      this.presenter.startTransforming(event)
    );
    const sourcecodeOption = document.createElement("option");
    sourcecodeOption.value = "--";
    sourcecodeOption.appendChild(document.createTextNode("--"));
    sourcecodeSelect.appendChild(sourcecodeOption);
    for (const lang of getSupportedCodeLanguages()) {
      const langDiv = document.createElement("option");
      langDiv.value = lang;
      langDiv.appendChild(document.createTextNode(lang));
      sourcecodeSelect.appendChild(langDiv);
    }

    const sourcecodeCopy = document.createElement("div");
    sourcecodeCopy.setAttribute("data-tooltip", t("code.copyCode"));
    sourcecodeCopy.classList.add(
      "sourcecodeCopyButton",
      "copyIcon",
      "struktoOption",
      "hand",
      "tooltip"
    );
    sourcecodeCopy.addEventListener("click", function (event) {
      navigator.clipboard.writeText(localStorage.getItem("struktog_code"));
    });

    sourcecodeForm.appendChild(sourcecodeSelect);
    sourcecodeHeader.appendChild(sourcecodeTitle);
    sourcecodeHeader.appendChild(sourcecodeForm);
    sourcecodeHeader.appendChild(sourcecodeCopy);

    const sourcecodeWorkingArea = document.createElement("div");
    sourcecodeWorkingArea.classList.add("columnAuto");
    sourcecodeWorkingArea.id = "Sourcecode";

    sourcecodeDisplay.appendChild(sourcecodeHeader);
    sourcecodeDisplay.appendChild(sourcecodeWorkingArea);
    sourcecode.appendChild(sourcecodeDisplay);

    this.domRoot = document.getElementById("Sourcecode");

    this.generateCodeSwitch(
      this.presenter,
      document.getElementById("struktoOptions1")
    );
    this.generateCodeSwitch(
      this.presenter,
      document.getElementById("struktoOptions2")
    );

    if (typeof Storage !== "undefined") {
      if ("displaySourcecode" in localStorage && "lang" in localStorage) {
        const possibleLang = localStorage.lang;
        if (possibleLang in this.translationMap) {
          this.lang = possibleLang;
          sourcecodeSelect.value = this.lang;
          this.presenter.setSourcecodeDisplay(
            JSON.parse(localStorage.displaySourcecode)
          );
          this.displaySourcecode("ToggleSourcecode");
        }
      }
    }
  }

  render(model) {
    // remove content
    while (this.domRoot.hasChildNodes()) {
      this.domRoot.removeChild(this.domRoot.lastChild);
    }

    // only translate, if some language is selected
    if (this.lang !== "--") {
      // check if translation is possible with current tree
      const isTranslatable = isSourceCodeTranslatable(model, this.lang);

      // create container for the spans
      const preBlock = document.createElement("pre");
      preBlock.classList.add("code");
      // set the language attribute
      preBlock.setAttribute("data-lang", this.lang);

      const codeBlock = document.createElement("code");

      // start appending the translated elements
      let codeText = "";
      if (isTranslatable) {
        const content = this.transformToCode(model, 0, this.lang);
        content.forEach(function (i) {
          codeBlock.appendChild(i);
          codeText = codeText + i.textContent;
        });
      } else {
        codeBlock.appendChild(document.createTextNode(t("code.unsupported")));
      }
      const generatedCode = generateSourceCode(model, this.lang);
      localStorage.setItem(
        "struktog_code",
        generatedCode.ok && generatedCode.supported
          ? generatedCode.code
          : codeText
      );

      preBlock.appendChild(codeBlock);
      this.domRoot.appendChild(preBlock);
    }
  }

  generateCodeSwitch(presenter, domNode) {
    const option = document.createElement("div");
    option.classList.add(
      "struktoOption",
      "codeIcon",
      "tooltip",
      "tooltip-bottomCode",
      "hand",
      "ToggleSourcecode"
    );
    option.setAttribute("data-tooltip", t("code.showSourcecode"));
    option.addEventListener("click", (event) =>
      presenter.alterSourcecodeDisplay(event)
    );
    domNode.appendChild(option);
  }

  setLang(lang) {
    if (typeof Storage !== "undefined") {
      localStorage.lang = lang;
      localStorage.displaySourcecode = true;
    }
    const sourcecodeSelect = document.getElementById("SourcecodeSelect");
    if (sourcecodeSelect) {
      sourcecodeSelect.value = lang;
    }
    this.lang = lang;
  }

  resetButtons() {}

  /**
   * Add indentations to a text element
   *
   * @param    indentLevel   number of how many levels deep the node is
   * @return   string        multiple indentations, times the level
   */
  addIndentations(indentLevel) {
    let text = "";
    const defaultIndent = "    ";
    for (let i = 0; i < indentLevel; i++) {
      text = text + defaultIndent;
    }
    return text;
  }

  /**
   * Create a span with text and a highlight class
   *
   * @param    text   text to be displayed
   * @return   span   complete HTML structure with text and class
   */
  createHighlightedSpan(text) {
    const span = document.createElement("span");
    span.classList.add("text-code");
    span.appendChild(document.createTextNode(text));
    return span;
  }

  /**
   * Check recursively elements if they match a given type
   *
   * @param    subTree    part of the tree with all children of current element
   * @param    nodeType   type of the translation map element to be checked against
   * @return   boolean    true, if the current element type is the given type
   */
  checkForUntranslatable(subTree, nodeType) {
    // end recursion
    if (
      subTree.type === "Placeholder" ||
      (subTree.type === "InsertNode" && subTree.followElement === null)
    ) {
      return false;
    } else {
      // compare the types1
      if (subTree.type === nodeType) {
        return true;
      } else {
        // different recursive steps, depending on child structure
        switch (subTree.type) {
          case "InsertNode":
          case "InputNode":
          case "OutputNode":
          case "TaskNode":
          case "BlockCallNode":
            return (
              false ||
              this.checkForUntranslatable(subTree.followElement, nodeType)
            );
          case "BranchNode":
            return (
              false ||
              this.checkForUntranslatable(subTree.trueChild, nodeType) ||
              this.checkForUntranslatable(subTree.falseChild, nodeType) ||
              this.checkForUntranslatable(subTree.followElement, nodeType)
            );
          case "TryCatchNode":
            return (
              false ||
              this.checkForUntranslatable(subTree.tryChild, nodeType) ||
              this.checkForUntranslatable(subTree.catchChild, nodeType) ||
              this.checkForUntranslatable(subTree.followElement, nodeType)
            );
          case "CountLoopNode":
          case "HeadLoopNode":
          case "FootLoopNode":
          case "FunctionNode":
            return (
              false ||
              this.checkForUntranslatable(subTree.child, nodeType) ||
              this.checkForUntranslatable(subTree.followElement, nodeType)
            );
          case "CaseNode": {
            let state = false;
            for (let i = 0; i < subTree.length; i++) {
              state =
                state ||
                this.checkForUntranslatable(subTree.cases[i], nodeType);
            }
            state =
              state ||
              this.checkForUntranslatable(subTree.defaultNode, nodeType);
            return (
              state ||
              this.checkForUntranslatable(subTree.followElement, nodeType)
            );
          }
        }
      }
    }
  }

  /**
   * Tranform an element to sourcecode with a translation mapping
   *
   * @param    subTree       part of the tree with all children of current element
   * @param    indentLevel   number of indentation levels
   * @param    lang          current sourcecode language
   * @return   []            array of span elements with the tranformed element
   */
  transformToCode(subTree, indentLevel, lang, switchVar = false) {
    // end recursion
    if (
      !subTree ||
      subTree.type === "Placeholder" ||
      (subTree.type === "InsertNode" && subTree.followElement === null)
    ) {
      return [];
    } else {
      // create the span
      const elemSpan = document.createElement("span");
      elemSpan.id = subTree.id + "-codeLine";
      // add eventlisteners for mouseover and click events
      // highlight equivalent element in struktogramm on mouseover
      elemSpan.addEventListener("mouseover", function () {
        const node = document.getElementById(subTree.id);
        node.firstChild.classList.add("highlight");
      });
      elemSpan.addEventListener("mouseout", function () {
        const node = document.getElementById(subTree.id);
        node.firstChild.classList.remove("highlight");
      });
      // switch to edit mode of equivalent element in the struktogramm
      const text = this.createHighlightedSpan(subTree.text);
      text.classList.add("hand");
      text.addEventListener("click", () =>
        this.presenter.switchEditState(subTree.id)
      );

      switch (subTree.type) {
        case "InsertNode":
          return this.transformToCode(subTree.followElement, indentLevel, lang);
        case "InputNode": {
          const inputPre = document.createElement("span");
          inputPre.classList.add("keyword");
          inputPre.appendChild(
            document.createTextNode(
              this.addIndentations(indentLevel) +
                this.translationMap[lang].InputNode.pre
            )
          );
          elemSpan.appendChild(inputPre);
          elemSpan.appendChild(text);
          const inputPost = document.createElement("span");
          inputPost.classList.add("keyword");
          inputPost.appendChild(
            document.createTextNode(this.translationMap[lang].InputNode.post)
          );
          elemSpan.appendChild(inputPost);
          return [elemSpan].concat(
            this.transformToCode(subTree.followElement, indentLevel, lang)
          );
        }
        case "OutputNode": {
          const outputPre = document.createElement("span");
          outputPre.classList.add("keyword");
          outputPre.appendChild(
            document.createTextNode(
              this.addIndentations(indentLevel) +
                this.translationMap[lang].OutputNode.pre
            )
          );
          elemSpan.appendChild(outputPre);
          elemSpan.appendChild(text);
          const outputPost = document.createElement("span");
          outputPost.classList.add("keyword");
          outputPost.appendChild(
            document.createTextNode(this.translationMap[lang].OutputNode.post)
          );
          elemSpan.appendChild(outputPost);
          return [elemSpan].concat(
            this.transformToCode(subTree.followElement, indentLevel, lang)
          );
        }
        case "TaskNode":
        case "BlockCallNode": {
          const taskMappingKey =
            subTree.type === "BlockCallNode" ? "TaskNode" : subTree.type;
          const taskPre = document.createElement("span");
          taskPre.classList.add("keyword");
          taskPre.appendChild(
            document.createTextNode(
              this.addIndentations(indentLevel) +
                this.translationMap[lang][taskMappingKey].pre
            )
          );
          elemSpan.appendChild(taskPre);
          elemSpan.appendChild(text);
          const taskPost = document.createElement("span");
          taskPost.classList.add("keyword");
          taskPost.appendChild(
            document.createTextNode(
              this.translationMap[lang][taskMappingKey].post
            )
          );
          elemSpan.appendChild(taskPost);
          return [elemSpan].concat(
            this.transformToCode(subTree.followElement, indentLevel, lang)
          );
        }
        case "BranchNode": {
          const branchHeaderPre = document.createElement("span");
          branchHeaderPre.classList.add("keyword");
          branchHeaderPre.appendChild(
            document.createTextNode(
              this.addIndentations(indentLevel) +
                this.translationMap[lang].BranchNode.pre
            )
          );
          elemSpan.appendChild(branchHeaderPre);
          elemSpan.appendChild(text);
          const branchHeaderPost = document.createElement("span");
          branchHeaderPost.classList.add("keyword");
          branchHeaderPost.appendChild(
            document.createTextNode(this.translationMap[lang].BranchNode.post)
          );
          elemSpan.appendChild(branchHeaderPost);
          let branch = [elemSpan];
          if (this.translationMap[lang].leftBracket !== "") {
            const leftBracket = document.createElement("span");
            leftBracket.appendChild(
              document.createTextNode(
                this.addIndentations(indentLevel) +
                  this.translationMap[lang].leftBracket +
                  "\n"
              )
            );
            branch.push(leftBracket);
          }
          const trueContent = this.transformToCode(
            subTree.trueChild,
            indentLevel + 1,
            lang
          );
          const falseContent = this.transformToCode(
            subTree.falseChild,
            indentLevel + 1,
            lang
          );
          branch = branch.concat(trueContent);
          if (falseContent.length > 0) {
            const between = document.createElement("span");
            between.appendChild(
              document.createTextNode(
                this.addIndentations(indentLevel) +
                  this.translationMap[lang].BranchNode.between
              )
            );
            branch.push(between);
          }
          branch = branch.concat(falseContent);
          if (this.translationMap[lang].rightBracket !== "") {
            const rightBracket = document.createElement("span");
            rightBracket.appendChild(
              document.createTextNode(
                this.addIndentations(indentLevel) +
                  this.translationMap[lang].rightBracket +
                  "\n"
              )
            );
            branch.push(rightBracket);
          }
          return branch.concat(
            this.transformToCode(subTree.followElement, indentLevel, lang)
          );
        }
        case "TryCatchNode": {
          const tryHeaderPre = newElement("span", ["keyword"], elemSpan);
          tryHeaderPre.appendChild(
            document.createTextNode(
              this.addIndentations(indentLevel) +
                this.translationMap[lang].TryCatchNode.pre
            )
          );
          let trycatch = [elemSpan];
          if (this.translationMap[lang].leftBracket !== "") {
            const leftBracket = document.createElement("span");
            leftBracket.appendChild(
              document.createTextNode(
                this.addIndentations(indentLevel) +
                  this.translationMap[lang].leftBracket +
                  "\n"
              )
            );
            trycatch.push(leftBracket);
          }
          const tryContent = this.transformToCode(
            subTree.tryChild,
            indentLevel + 1,
            lang
          ); // try Content
          trycatch = trycatch.concat(tryContent);

          if (this.translationMap[lang].rightBracket !== "") {
            const rightBracket = document.createElement("span");
            rightBracket.appendChild(
              document.createTextNode(
                this.addIndentations(indentLevel) +
                  this.translationMap[lang].rightBracket +
                  "\n"
              )
            );
            trycatch.push(rightBracket);
          }

          subTree.catches.forEach((catchNode) => {
            const catchBetween = newElement("span", ["keyword"]);
            catchBetween.appendChild(
              document.createTextNode(
                this.addIndentations(indentLevel) +
                  this.translationMap[lang].TryCatchNode.between
              )
            );
            // only insert space if a parameter is set for the catch block
            if (
              catchNode.text &&
              catchNode.text !== "" &&
              lang.includes("Python")
            ) {
              catchBetween.appendChild(document.createTextNode(" "));
            }
            // catchBetween.appendChild(text)
            catchBetween.appendChild(document.createTextNode(catchNode.text));
            trycatch.push(catchBetween);
            const catchBetweenPost = newElement("span", ["keyword"]);
            catchBetweenPost.appendChild(
              document.createTextNode(
                this.translationMap[lang].TryCatchNode.post
              )
            );
            trycatch.push(catchBetweenPost);
            const catchContent = this.transformToCode(
              catchNode,
              indentLevel + 1,
              lang
            ); // catch content
            if (this.translationMap[lang].leftBracket !== "") {
              const leftBracket = document.createElement("span");
              leftBracket.appendChild(
                document.createTextNode(
                  this.addIndentations(indentLevel) +
                    this.translationMap[lang].leftBracket +
                    "\n"
                )
              );
              trycatch.push(leftBracket);
            }
            trycatch = trycatch.concat(catchContent);
            if (this.translationMap[lang].rightBracket !== "") {
              const rightBracket = document.createElement("span");
              rightBracket.appendChild(
                document.createTextNode(
                  this.addIndentations(indentLevel) +
                    this.translationMap[lang].rightBracket +
                    "\n"
                )
              );
              trycatch.push(rightBracket);
            }
          });
          return trycatch.concat(
            this.transformToCode(subTree.followElement, indentLevel, lang)
          );
        }
        case "CountLoopNode": {
          const loopHeaderPre = document.createElement("span");
          loopHeaderPre.classList.add("keyword");
          loopHeaderPre.appendChild(
            document.createTextNode(
              this.addIndentations(indentLevel) +
                this.translationMap[lang].CountLoopNode.pre
            )
          );
          elemSpan.appendChild(loopHeaderPre);
          elemSpan.appendChild(text);
          const loopHeaderPost = document.createElement("span");
          loopHeaderPost.classList.add("keyword");
          loopHeaderPost.appendChild(
            document.createTextNode(
              this.translationMap[lang].CountLoopNode.post
            )
          );
          elemSpan.appendChild(loopHeaderPost);
          let loop = [elemSpan];
          if (this.translationMap[lang].leftBracket !== "") {
            const leftBracket = document.createElement("span");
            leftBracket.appendChild(
              document.createTextNode(
                this.addIndentations(indentLevel) +
                  this.translationMap[lang].leftBracket +
                  "\n"
              )
            );
            loop.push(leftBracket);
          }
          loop = loop.concat(
            this.transformToCode(subTree.child, indentLevel + 1, lang)
          );
          if (this.translationMap[lang].rightBracket !== "") {
            const rightBracket = document.createElement("span");
            rightBracket.appendChild(
              document.createTextNode(
                this.addIndentations(indentLevel) +
                  this.translationMap[lang].rightBracket +
                  "\n"
              )
            );
            loop.push(rightBracket);
          }
          return loop.concat(
            this.transformToCode(subTree.followElement, indentLevel, lang)
          );
        }
        case "HeadLoopNode": {
          const loopHeaderPre = document.createElement("span");
          loopHeaderPre.classList.add("keyword");
          loopHeaderPre.appendChild(
            document.createTextNode(
              this.addIndentations(indentLevel) +
                this.translationMap[lang].HeadLoopNode.pre
            )
          );
          elemSpan.appendChild(loopHeaderPre);
          elemSpan.appendChild(text);
          const loopHeaderPost = document.createElement("span");
          loopHeaderPost.classList.add("keyword");
          loopHeaderPost.appendChild(
            document.createTextNode(this.translationMap[lang].HeadLoopNode.post)
          );
          elemSpan.appendChild(loopHeaderPost);
          let loop = [elemSpan];
          if (this.translationMap[lang].leftBracket !== "") {
            const leftBracket = document.createElement("span");
            leftBracket.appendChild(
              document.createTextNode(
                this.addIndentations(indentLevel) +
                  this.translationMap[lang].leftBracket +
                  "\n"
              )
            );
            loop.push(leftBracket);
          }
          loop = loop.concat(
            this.transformToCode(subTree.child, indentLevel + 1, lang)
          );
          if (this.translationMap[lang].rightBracket !== "") {
            const rightBracket = document.createElement("span");
            rightBracket.appendChild(
              document.createTextNode(
                this.addIndentations(indentLevel) +
                  this.translationMap[lang].rightBracket +
                  "\n"
              )
            );
            loop.push(rightBracket);
          }
          return loop.concat(
            this.transformToCode(subTree.followElement, indentLevel, lang)
          );
        }
        case "FootLoopNode": {
          const loopContent = document.createElement("span");
          loopContent.classList.add("keyword");
          loopContent.appendChild(
            document.createTextNode(
              this.addIndentations(indentLevel) +
                this.translationMap[lang].FootLoopNode.prepre
            )
          );
          elemSpan.appendChild(loopContent);
          let loop = [elemSpan];
          if (this.translationMap[lang].leftBracket !== "") {
            const leftBracket = document.createElement("span");
            leftBracket.appendChild(
              document.createTextNode(
                this.addIndentations(indentLevel) +
                  this.translationMap[lang].leftBracket +
                  "\n"
              )
            );
            loop.push(leftBracket);
          }
          const child = this.transformToCode(
            subTree.child,
            indentLevel + 1,
            lang
          );
          loop = loop.concat(child);
          if (this.translationMap[lang].rightBracket !== "") {
            const rightBracket = document.createElement("span");
            rightBracket.appendChild(
              document.createTextNode(
                this.addIndentations(indentLevel) +
                  this.translationMap[lang].rightBracket +
                  "\n"
              )
            );
            loop.push(rightBracket);
          }
          const subContent = document.createElement("span");
          subContent.id = subTree.id + "-codeLine";
          elemSpan.id = "";
          const subContentPre = document.createElement("span");
          subContentPre.classList.add("keyword");
          subContentPre.appendChild(
            document.createTextNode(
              this.addIndentations(indentLevel) +
                this.translationMap[lang].FootLoopNode.pre
            )
          );
          subContent.appendChild(subContentPre);
          subContent.appendChild(text);
          const subContentPost = document.createElement("span");
          subContentPost.classList.add("keyword");
          subContentPost.appendChild(
            document.createTextNode(this.translationMap[lang].FootLoopNode.post)
          );
          subContent.appendChild(subContentPost);
          subContent.addEventListener("mouseover", function () {
            const node = document.getElementById(subTree.id);
            node.firstChild.classList.add("highlight");
          });
          subContent.addEventListener("mouseout", function () {
            const node = document.getElementById(subTree.id);
            node.firstChild.classList.remove("highlight");
          });
          loop.push(subContent);

          return loop.concat(
            this.transformToCode(subTree.followElement, indentLevel, lang)
          );
        }
        case "FunctionNode": {
          let functionPre = this.translationMap[lang].FunctionNode.pre;
          const typedLanguages = ["Java", "C#", "C++", "C"];
          const returnType = (subTree.returnType || "").trim();
          if (typedLanguages.includes(lang) && returnType !== "") {
            functionPre = functionPre.replace(/void\s+$/, returnType + " ");
          }

          const functionContent = document.createElement("span");
          functionContent.classList.add("keyword");
          functionContent.appendChild(
            document.createTextNode(
              this.addIndentations(indentLevel) + functionPre
            )
          );
          elemSpan.appendChild(functionContent);
          elemSpan.appendChild(text);
          const funcHeaderBetween = document.createElement("span");
          funcHeaderBetween.classList.add("keyword");
          funcHeaderBetween.appendChild(
            document.createTextNode(
              this.translationMap[lang].FunctionNode.between
            )
          );
          elemSpan.appendChild(funcHeaderBetween);

          // add parameters
          const params = subTree.parameters;
          let parCount = 0;
          for (const par of params) {
            if (parCount !== 0) {
              elemSpan.appendChild(this.createHighlightedSpan(", "));
            }
            const paramName = this.createHighlightedSpan(par.parName);
            paramName.classList.add("hand");
            // mapping the stored positions (0, 3, 6, ...) to new positions (0, 2, 4, ...)
            paramName.addEventListener("click", () =>
              this.presenter.switchEditState(subTree.id, (par.pos / 3) * 2)
            );
            elemSpan.appendChild(paramName);
            parCount += 1;
          }

          const funcHeaderPost = document.createElement("span");
          funcHeaderPost.classList.add("keyword");
          funcHeaderPost.appendChild(
            document.createTextNode(this.translationMap[lang].FunctionNode.post)
          );
          elemSpan.appendChild(funcHeaderPost);
          let loop = [elemSpan];
          if (this.translationMap[lang].leftBracket !== "") {
            const leftBracket = document.createElement("span");
            leftBracket.appendChild(
              document.createTextNode(
                this.addIndentations(indentLevel) +
                  this.translationMap[lang].leftBracket +
                  "\n"
              )
            );
            loop.push(leftBracket);
          }
          loop = loop.concat(
            this.transformToCode(subTree.child, indentLevel + 1, lang)
          );
          if (this.translationMap[lang].rightBracket !== "") {
            const rightBracket = document.createElement("span");
            rightBracket.appendChild(
              document.createTextNode(
                this.addIndentations(indentLevel) +
                  this.translationMap[lang].rightBracket +
                  "\n"
              )
            );
            loop.push(rightBracket);
          }
          return loop.concat(
            this.transformToCode(subTree.followElement, indentLevel, lang)
          );
        }
        case "CaseNode": {
          if (!this.translationMap[lang].pseudoSwitch) {
            const caseHeadPre = document.createElement("span");
            caseHeadPre.classList.add("keyword");
            caseHeadPre.appendChild(
              document.createTextNode(
                this.addIndentations(indentLevel) +
                  this.translationMap[lang].CaseNode.pre
              )
            );
            elemSpan.appendChild(caseHeadPre);
            elemSpan.appendChild(text);

            const caseHeadPost = document.createElement("span");
            caseHeadPost.classList.add("keyword");
            caseHeadPost.appendChild(
              document.createTextNode(this.translationMap[lang].CaseNode.post)
            );
            elemSpan.appendChild(caseHeadPost);
          }
          let cases = [elemSpan];
          if (this.translationMap[lang].pseudoSwitch) {
            cases = [];
          }
          if (this.translationMap[lang].leftBracket !== "") {
            const leftBracket = document.createElement("span");
            leftBracket.appendChild(
              document.createTextNode(
                this.addIndentations(indentLevel) +
                  this.translationMap[lang].leftBracket +
                  "\n"
              )
            );
            cases.push(leftBracket);
          }
          for (const element of subTree.cases) {
            if (this.translationMap[lang].pseudoSwitch) {
              const switchVarSpan = this.createHighlightedSpan(subTree.text);
              switchVarSpan.classList.add("hand");
              switchVarSpan.addEventListener("click", () =>
                this.presenter.switchEditState(subTree.id)
              );
              cases = cases.concat(
                this.transformToCode(element, indentLevel, lang, switchVarSpan)
              );
            } else {
              cases = cases.concat(
                this.transformToCode(element, indentLevel + 1, lang)
              );
            }
          }
          if (this.translationMap[lang].pseudoSwitch) {
            cases[0].firstChild.innerText = "if ";
          }
          if (subTree.defaultOn) {
            const defaultCase = document.createElement("span");
            defaultCase.classList.add("keyword");
            defaultCase.id = subTree.defaultNode.id + "-codeLine";
            if (this.translationMap[lang].pseudoSwitch) {
              defaultCase.appendChild(
                document.createTextNode(
                  this.addIndentations(indentLevel) +
                    this.translationMap[lang].InsertCase.preDefault +
                    this.translationMap[lang].InsertCase.post
                )
              );
            } else {
              defaultCase.appendChild(
                document.createTextNode(
                  this.addIndentations(indentLevel + 1) +
                    this.translationMap[lang].InsertCase.preDefault +
                    this.translationMap[lang].InsertCase.post
                )
              );
            }
            defaultCase.addEventListener("mouseover", function () {
              const node = document.getElementById(subTree.defaultNode.id);
              node.firstChild.classList.add("highlight");
            });
            defaultCase.addEventListener("mouseout", function () {
              const node = document.getElementById(subTree.defaultNode.id);
              node.firstChild.classList.remove("highlight");
            });
            cases.push(defaultCase);
            if (this.translationMap[lang].pseudoSwitch) {
              cases = cases.concat(
                this.transformToCode(
                  subTree.defaultNode.followElement,
                  indentLevel + 1,
                  lang
                )
              );
            } else {
              cases = cases.concat(
                this.transformToCode(
                  subTree.defaultNode.followElement,
                  indentLevel + 2,
                  lang
                )
              );
            }
            if (
              !this.translationMap[lang].pseudoSwitch &&
              (lang === "C#" || lang === "Java")
            ) {
              const endContent = document.createElement("span");
              endContent.classList.add("keyword");
              endContent.appendChild(
                document.createTextNode(
                  this.addIndentations(indentLevel + 2) +
                    this.translationMap[lang].InsertCase.postpost
                )
              );
              cases.push(endContent);
            }
          }
          if (this.translationMap[lang].rightBracket !== "") {
            const rightBracket = document.createElement("span");
            rightBracket.appendChild(
              document.createTextNode(
                this.addIndentations(indentLevel) +
                  this.translationMap[lang].rightBracket +
                  "\n"
              )
            );
            cases.push(rightBracket);
          }
          return cases.concat(
            this.transformToCode(subTree.followElement, indentLevel, lang)
          );
        }
        case "InsertCase": {
          const casePre = document.createElement("span");
          casePre.classList.add("keyword");
          casePre.appendChild(
            document.createTextNode(
              this.addIndentations(indentLevel) +
                this.translationMap[lang].InsertCase.preNormal
            )
          );
          elemSpan.appendChild(casePre);
          if (switchVar) {
            elemSpan.appendChild(switchVar);
            const equals = document.createElement("span");
            equals.appendChild(document.createTextNode(" == "));
            elemSpan.appendChild(equals);
          }
          elemSpan.appendChild(text);
          const casePost = document.createElement("span");
          casePost.classList.add("keyword");
          casePost.appendChild(
            document.createTextNode(this.translationMap[lang].InsertCase.post)
          );
          elemSpan.appendChild(casePost);
          let content = [elemSpan];
          content = content.concat(
            this.transformToCode(subTree.followElement, indentLevel + 1, lang)
          );

          if (!this.translationMap[lang].pseudoSwitch) {
            const endContent = document.createElement("span");
            endContent.classList.add("keyword");
            endContent.appendChild(
              document.createTextNode(
                this.addIndentations(indentLevel + 1) +
                  this.translationMap[lang].InsertCase.postpost
              )
            );
            content.push(endContent);
          }
          return content;
        }
      }
    }
  }

  /**
   * Get the currently selected code language
   */
  prepareTransforming() {
    const lang = document.getElementById("SourcecodeSelect").value;
    // start the transformation
    presenter.startTransforming(lang);
  }

  /**
   * Toggle the state of the sourcecode display button
   *
   * @param   buttonId   id of the sourcecode display toggle button
   */
  displaySourcecode(buttonClass) {
    const fields = document.getElementsByClassName(buttonClass);
    const sourcecodeDisplay = document.getElementById("SourcecodeDisplay");
    const editorContent = document.getElementById("editorContent");
    const structogram = document.getElementById("structogram");
    const hasStructogram = Boolean(structogram);
    const isFullWidth =
      hasStructogram && structogram.classList.contains("structogramFullWidth");

    if (this.presenter.getSourcecodeDisplay()) {
      for (const item of fields) {
        item.setAttribute("data-tooltip", "Quellcode ausblenden");
      }
      sourcecodeDisplay.style.display = "block";
      if (editorContent) {
        editorContent.style.flexBasis = "75%";
      }
      if (hasStructogram && !isFullWidth) {
        structogram.classList.add("structogramFullWidth");
        this.sourcecodeActivatedFullWidth = true;
      } else {
        this.sourcecodeActivatedFullWidth = false;
      }
    } else {
      for (const item of fields) {
        item.setAttribute("data-tooltip", t("code.showSourcecode"));
      }
      sourcecodeDisplay.style.display = "none";
      if (editorContent) {
        editorContent.style.flexBasis = "100%";
      }
      if (hasStructogram && this.sourcecodeActivatedFullWidth) {
        structogram.classList.remove("structogramFullWidth");
      }
      this.sourcecodeActivatedFullWidth = false;
    }

    const toggleButtons = document.getElementsByClassName("ToggleEditorWidth");
    const tooltipText =
      hasStructogram && structogram.classList.contains("structogramFullWidth")
        ? t("editor.limitEditorWidth")
        : t("editor.expandEditorWidth");
    for (const button of toggleButtons) {
      button.setAttribute("data-tooltip", tooltipText);
      button.classList.toggle(
        "struktoOptionActive",
        hasStructogram && structogram.classList.contains("structogramFullWidth")
      );
    }
  }
}
